// components/FloatingCanvasInput.tsx
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ArrowUp, Trash2 } from '@/components/ui/lucide-memo';

interface FloatingCanvasInputAvatar {
  imageUrl?: string;
  alt: string;
  color?: string;
}

interface FloatingCanvasInputProps {
  value: string;
  placeholder: string;
  submitTitle: string;
  avatar: FloatingCanvasInputAvatar;
  left: string | number;
  top: string | number;
  transform?: string;
  deleteTitle?: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onDelete?: () => void;
}

const AVATAR_BUBBLE_STYLE: React.CSSProperties = {
  width: 32,
  height: 32,
  backgroundColor: '#2e2e2e',
  borderRadius: '50% 50% 50% 2px',
};

const AVATAR_IMAGE_STYLE: React.CSSProperties = { width: 26, height: 26 };

const FloatingCanvasInput: React.FC<FloatingCanvasInputProps> = ({
  value,
  placeholder,
  submitTitle,
  avatar,
  left,
  top,
  transform,
  deleteTitle = 'Delete',
  onChange,
  onSubmit,
  onCancel,
  onDelete,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const canSubmit = value.trim().length > 0;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const containerStyle = useMemo<React.CSSProperties>(
    () => ({ left, top, transform, transformOrigin: 'top left' }),
    [left, top, transform],
  );

  const avatarFallbackStyle = useMemo<React.CSSProperties>(
    () => ({ width: 26, height: 26, backgroundColor: avatar.color ?? '#3B82F6' }),
    [avatar.color],
  );

  const stopPropagation = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (canSubmit) onSubmit();
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    },
    [canSubmit, onSubmit, onCancel],
  );

  return (
    <div
      className="absolute z-50 pointer-events-auto"
      style={containerStyle}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onContextMenu={handleContextMenu}
    >
      <div className="flex items-end gap-2">
        <div
          className="flex shrink-0 items-center justify-center shadow-lg"
          style={AVATAR_BUBBLE_STYLE}
        >
          {avatar.imageUrl ? (
            <img
              src={avatar.imageUrl}
              alt={avatar.alt}
              className="rounded-full object-cover"
              style={AVATAR_IMAGE_STYLE}
            />
          ) : (
            <div
              className="rounded-full"
              style={avatarFallbackStyle}
            />
          )}
        </div>
        <div className="flex items-center gap-1 rounded-full border border-white/10 bg-[#3a3a3a]/95 py-1.5 pl-4 pr-1.5 shadow-2xl backdrop-blur-sm">
          <input
            ref={inputRef}
            value={value}
            placeholder={placeholder}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            className="w-44 appearance-none border-0 bg-transparent text-sm text-white placeholder-white/40 outline-none focus:outline-none focus:ring-0"
          />
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              title={deleteTitle}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white/50 transition-colors hover:bg-white/10 hover:text-red-400"
            >
              <Trash2 size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            title={submitTitle}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 disabled:opacity-40 disabled:hover:bg-white/15"
          >
            <ArrowUp size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FloatingCanvasInput;
